import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import logo from "../../assets/images/kmp3.png";
import { HiOutlineLocationMarker } from "react-icons/hi";

const Footer = () => {
  const glowRef = useRef(null);

  /* =========================================================
      PARALLAX GLOW
  ========================================================= */

  const handleMouseMove = (e) => {
    const moveX =
      (e.clientX - window.innerWidth / 2) *
      0.015;

    const moveY =
      (e.clientY - window.innerHeight / 2) *
      0.015;

    if (glowRef.current) {
      glowRef.current.style.transform = `translate(${moveX}px, ${moveY}px)`;
    }
  };

  return (
    <footer
      onMouseMove={handleMouseMove}
      className="
      relative
      overflow-hidden
      bg-[linear-gradient(135deg,#040816,#07111f,#0b1220)]
      text-white
    "
    >
      {/* =========================================================
          TOP PREMIUM LINE
      ========================================================= */}

      <div
        className="
        h-[3px]
        w-full
        bg-gradient-to-r
        from-blue-500
        via-[#F7941D]
        to-indigo-500
      "
      />

      {/* =========================================================
          PREMIUM BACKGROUND
      ========================================================= */}

      {/* MAIN BLUE GLOW */}
      <div
        ref={glowRef}
        className="
        pointer-events-none
        absolute
        -top-40
        -left-40
        w-[360px]
        h-[360px]
        rounded-full
        bg-blue-500/20
        blur-[130px]
        transition-transform
        duration-300
      "
      />

      {/* ORANGE GLOW */}
      <div
        className="
        absolute
        bottom-[-120px]
        right-[-120px]
        w-[340px]
        h-[340px]
        rounded-full
        bg-[#F7941D]/20
        blur-[130px]
      "
      />

      {/* CENTER INDIGO GLOW */}
      <div
        className="
        absolute
        top-[25%]
        left-[40%]
        w-[260px]
        h-[260px]
        rounded-full
        bg-indigo-500/10
        blur-[120px]
      "
      />

      {/* GRID */}
      <div
        className="
        absolute inset-0
        opacity-[0.05]
        bg-[linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)]
        bg-[size:60px_60px]
      "
      />

      {/* FLOATING SHAPES */}

      <motion.div
        animate={{
          y: [-12, 12, -12],
          rotate: [10, 18, 10],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
        absolute
        top-[12%]
        left-[7%]
        w-20
        h-20
        rounded-[26px]
        border
        border-white/10
        bg-white/5
        backdrop-blur-xl
        rotate-12
      "
      />

      <motion.div
        animate={{
          y: [10, -10, 10],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
        absolute
        bottom-[18%]
        right-[14%]
        w-5
        h-5
        rounded-full
        bg-[#F7941D]/60
      "
      />

      {/* =========================================================
          CONTENT
      ========================================================= */}

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.18,
            },
          },
        }}
        className="
        relative z-10
        max-w-7xl
        mx-auto
        px-6
        py-20
        lg:py-24
      "
      >
        {/* =========================================================
            GRID
        ========================================================= */}

        <div
          className="
          grid
          md:grid-cols-2
          lg:grid-cols-3
          gap-y-14
          gap-x-12
          items-start
        "
        >
          {/* =========================================================
              BRAND
          ========================================================= */}

          <motion.div
            variants={{
              hidden: {
                opacity: 0,
                y: 40,
              },
              show: {
                opacity: 1,
                y: 0,
              },
            }}
            className="space-y-6 max-w-sm"
          >
            {/* LOGO CARD */}
            <div
              className="
              inline-flex
              items-center
              justify-center
              rounded-[24px]
              border
              border-white/30
              bg-white/[0.04]
              backdrop-blur-xl
              px-5
              py-4
              shadow-[0_10px_40px_rgba(0,0,0,0.25)]
            "
            >
              <img
                src={logo}
                alt="KMP Logo"
                className="h-[62px] object-contain"
              />
            </div>

            <p
              className="
              text-slate-300
              text-[15px]
              leading-relaxed
            "
            >
              Providing expert business
              processes, outsourcing
              solutions, compliance
              management and digital
              transformation services with
              modern enterprise standards.
            </p>

            {/* TAGS */}
            <div className="flex flex-wrap gap-3 pt-2">
              {[
                "Business Solutions",
                "Compliance",
                "Security",
              ].map((tag, i) => (
                <div
                  key={i}
                  className="
                  px-4
                  py-2
                  rounded-full
                  border-[1.4px]
                  border-white/30
                  bg-white/[0.05]
                  text-[12px]
                  font-medium
                  text-slate-200
                  backdrop-blur-xl
                "
                >
                  {tag}
                </div>
              ))}
            </div>
          </motion.div>

          {/* =========================================================
              QUICK LINKS
          ========================================================= */}

          <motion.div
            variants={{
              hidden: {
                opacity: 0,
                y: 40,
              },
              show: {
                opacity: 1,
                y: 0,
              },
            }}
            className="lg:mx-auto w-full max-w-[230px]"
          >
            {/* HEADING */}
            <div className="mb-7">
              <h4
                className="
                text-[12px]
                tracking-[3px]
                uppercase
                font-bold
                text-white
              "
              >
                Quick Links
              </h4>

              <div
                className="
                mt-3
                h-[2px]
                w-12
                rounded-full
                bg-gradient-to-r
                from-blue-500
                to-indigo-400
              "
              />
            </div>

            <ul className="space-y-5">
              {[
                {
                  name: "Home",
                  link: "/home",
                },
                {
                  name: "About Us",
                  link: "/about",
                },
                {
                  name: "Services",
                  link: "/service",
                },
                {
                  name: "Contact Us",
                  link: "/contact",
                },
              ].map((item, i) => (
                <motion.li
                  key={i}
                  whileHover={{
                    x: 6,
                  }}
                >
                  <Link
                    to={item.link}
                    className="
                    group
                    relative
                    inline-flex
                    items-center
                    text-[15px]
                    font-medium
                    text-slate-300
                    hover:text-white
                    transition-all
                    duration-300
                  "
                  >
                    <span>{item.name}</span>

                    {/* UNDERLINE */}
                    <span
                      className="
                      absolute
                      left-0
                      -bottom-[5px]
                      h-[2px]
                      w-0
                      rounded-full
                      bg-gradient-to-r
                      from-blue-500
                      to-indigo-400
                      transition-all
                      duration-500
                      group-hover:w-full
                    "
                    />
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* =========================================================
              CONTACT
          ========================================================= */}

          <motion.div
            variants={{
              hidden: {
                opacity: 0,
                y: 40,
              },
              show: {
                opacity: 1,
                y: 0,
              },
            }}
            className="w-full max-w-[340px]"
          >
            {/* HEADING */}
            <div className="mb-7">
              <h4
                className="
                text-[12px]
                tracking-[3px]
                uppercase
                font-bold
                text-white
              "
              >
                Our Presence
              </h4>

              <div
                className="
                mt-3
                h-[2px]
                w-12
                rounded-full
                bg-gradient-to-r
                from-[#F7941D]
                to-orange-400
              "
              />
            </div>

            <div className="space-y-7">

              {/* REGISTERED */}
              <div
                className="
                group
                relative
                overflow-hidden
                rounded-[24px]
                border-2
                border-white/40
                bg-white/[0.04]
                backdrop-blur-xl
                p-5
                transition-all
                duration-300
                hover:border-orange-400/30
                hover:bg-white/[0.06]
              "
              >
                {/* SHINE */}
                <div
                  className="
                  absolute inset-0
                  bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.12),transparent)]
                  translate-x-[-120%]
                  group-hover:translate-x-[120%]
                  transition
                  duration-[1400ms]
                "
                />

                <div className="relative z-10 flex items-start gap-4">

                  <div
                    className="
                    w-11
                    h-11
                    rounded-2xl
                    flex
                    items-center
                    justify-center
                    bg-[#F7941D]/15
                    text-[#F7941D]
                    border
                    border-[#F7941D]/20
                    flex-shrink-0
                  "
                  >
                    <HiOutlineLocationMarker
                      size={20}
                    />
                  </div>

                  <div>
                    <p
                      className="
                      text-white
                      font-semibold
                      mb-2
                      text-[15px]
                    "
                    >
                      Registered Office
                    </p>

                    <p
                      className="
                      text-slate-300
                      text-[14px]
                      leading-relaxed
                    "
                    >
                      2129, Dhananjay Eternia,
                      Vijaya Nagar Colony,
                      Behind SP College,
                      Sadashiv Peth,
                      Pune - 411030
                    </p>
                  </div>

                </div>
              </div>

              {/* BRANCH */}
              <div
                className="
                group
                relative
                overflow-hidden
                rounded-[24px]
                border-2
                border-white/30
                bg-white/[0.04]
                backdrop-blur-xl
                p-5
                transition-all
                duration-300
                hover:border-blue-400/30
                hover:bg-white/[0.06]
              "
              >
                {/* SHINE */}
                <div
                  className="
                  absolute inset-0
                  bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.12),transparent)]
                  translate-x-[-120%]
                  group-hover:translate-x-[120%]
                  transition
                  duration-[1400ms]
                "
                />

                <div className="relative z-10 flex items-start gap-4">

                  <div
                    className="
                    w-11
                    h-11
                    rounded-2xl
                    flex
                    items-center
                    justify-center
                    bg-blue-500/15
                    text-blue-400
                    border
                    border-blue-400/20
                    flex-shrink-0
                  "
                  >
                    <HiOutlineLocationMarker
                      size={20}
                    />
                  </div>

                  <div>
                    <p
                      className="
                      text-white
                      font-semibold
                      mb-2
                      text-[15px]
                    "
                    >
                      Branch Office
                    </p>

                    <p
                      className="
                      text-slate-300
                      text-[14px]
                      leading-relaxed
                    "
                    >
                      Flat No. 12, Ground
                      Floor, Harshad Society,
                      Survey No: 9/2A & 9/2B,
                      Kothrud,
                      Pune - 411038
                    </p>
                  </div>

                </div>
              </div>

            </div>
          </motion.div>
        </div>

        {/* =========================================================
            DIVIDER
        ========================================================= */}

        <motion.div
          initial={{
            opacity: 0,
            scaleX: 0,
          }}
          whileInView={{
            opacity: 1,
            scaleX: 1,
          }}
          transition={{
            duration: 1,
          }}
          className="
          mt-16
          relative
          origin-left
        "
        >
          <div className="border-t border-white/10" />

          <div
            className="
            absolute
            top-0
            left-0
            w-full
            h-[2px]
            bg-gradient-to-r
            from-transparent
            via-blue-400/40
            to-transparent
            blur-sm
            opacity-80
          "
          />
        </motion.div>

        {/* =========================================================
            BOTTOM
        ========================================================= */}

        <motion.div
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          transition={{
            delay: 0.3,
          }}
          className="
          pt-7
          text-center
          text-slate-400
          text-[14px]
        "
        >
          ©{" "}
          <motion.span
            initial={{
              opacity: 0,
            }}
            whileInView={{
              opacity: 1,
            }}
            transition={{
              duration: 1,
            }}
            className="inline-block"
          >
            {new Date().getFullYear()}
          </motion.span>{" "}
          <span className="text-white font-medium">
            KMP Business Solution Private
            Limited
          </span>
          . All rights reserved.
        </motion.div>
      </motion.div>

      {/* =========================================================
          BOTTOM FADE
      ========================================================= */}

      <div
        className="
        absolute
        bottom-0
        left-0
        w-full
        h-20
        bg-gradient-to-t
        from-[#020617]
        to-transparent
      "
      />
    </footer>
  );
};

export default Footer;